import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, AlertCircle, Eye, EyeOff, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";

/**
 * Reset Password Page
 *
 * Allows users to set a new password using a reset token from email.
 * Validates:
 * - Token presence and validity
 * - Password strength requirements
 * - Password confirmation match
 *
 * Brand: Partner Portal uses Purple/Blue gradient theme
 * Colors: #282828 (signal-black), #4c1d95 (deep-purple), #1283EB (electric-blue)
 */

interface PasswordStrength {
  score: number;
  label: string;
  color: string;
}

export function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const token = searchParams.get("token");

  useEffect(() => {
    if (!token) {
      setError("Invalid or missing reset token. Please request a new password reset link.");
    }
  }, [token]);

  const calculatePasswordStrength = (pwd: string): PasswordStrength => {
    let score = 0;

    if (pwd.length >= 8) score++;
    if (pwd.length >= 12) score++;
    if (/[a-z]/.test(pwd) && /[A-Z]/.test(pwd)) score++;
    if (/\d/.test(pwd)) score++;
    if (/[^A-Za-z0-9]/.test(pwd)) score++;

    if (score <= 2) return { score, label: "Weak", color: "bg-red-500" };
    if (score <= 3) return { score, label: "Medium", color: "bg-yellow-500" };
    if (score <= 4) return { score, label: "Strong", color: "bg-green-500" };
    return { score, label: "Very Strong", color: "bg-green-600" };
  };

  const passwordStrength = password ? calculatePasswordStrength(password) : null;

  const validatePassword = (): string | null => {
    if (password.length < 8) {
      return "Password must be at least 8 characters";
    }
    if (!/[A-Z]/.test(password)) {
      return "Password must contain at least one uppercase letter";
    }
    if (!/[a-z]/.test(password)) {
      return "Password must contain at least one lowercase letter";
    }
    if (!/\d/.test(password)) {
      return "Password must contain at least one number";
    }
    if (!/[^A-Za-z0-9]/.test(password)) {
      return "Password must contain at least one special character";
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate password
    const passwordError = validatePassword();
    if (passwordError) {
      setError(passwordError);
      return;
    }

    // Check password match
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch("/api/auth/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || "Password reset failed");
      }

      setIsSuccess(true);

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate("/login");
      }, 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to reset password");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a]">
      {/* Enhanced gradient background with electric blue accent */}
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1283EB]/15 via-[#4c1d95]/30 to-[#282828]" />
        <div className="absolute inset-0 bg-gradient-to-tl from-[#1283EB]/10 via-transparent to-transparent" />
      </div>

      <div className="max-w-md w-full space-y-8 p-10 bg-[#1a1a1a]/90 backdrop-blur-sm rounded-2xl border border-[#1283EB]/20 shadow-2xl mx-4">
        {/* Logo/Header */}
        <div className="text-center">
          <img
            src="/aiia-logo-white.svg"
            alt="aiia logo"
            className="h-14 mx-auto mb-6"
          />
          <h1 className="text-xl text-white font-semibold">
            Set New Password
          </h1>
          <p className="mt-2 text-sm text-white/60">
            Choose a strong password for your account
          </p>
        </div>

        {isSuccess ? (
          <div className="space-y-6">
            <Alert className="bg-emerald-950/50 border-emerald-800 text-emerald-200">
              <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
              <AlertDescription>
                Your password has been reset successfully! Redirecting to login...
              </AlertDescription>
            </Alert>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-500/20 mb-4">
                <CheckCircle2 className="h-8 w-8 text-emerald-400" aria-hidden="true" />
              </div>
              <p className="text-sm text-white/60 mb-6">
                You can now sign in with your new password
              </p>
              <Link
                to="/login"
                className="inline-flex items-center justify-center w-full h-13 px-4 bg-[#1283EB] hover:bg-[#1283EB]/90 text-white font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#1283EB] focus:ring-offset-2 focus:ring-offset-[#1a1a1a]"
              >
                Go to login
              </Link>
            </div>
          </div>
        ) : (
          <>
            {/* Back to Login Link */}
            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-white/60 hover:text-white/90 transition-colors focus:outline-none focus:text-white"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              <span className="text-sm">Back to login</span>
            </Link>

            {/* Error Alert */}
            {error && (
              <Alert variant="destructive" className="bg-red-950/50 border-red-800 text-red-200">
                <AlertCircle className="h-4 w-4" aria-hidden="true" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Password Input */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-white/80 mb-1.5">
                  New Password
                </label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    required
                    aria-required="true"
                    aria-describedby="password-requirements"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Create a strong password"
                    className="h-12 bg-[#0a0a0a] border-white/10 text-white placeholder:text-white/40 focus:border-[#1283EB] focus:ring-2 focus:ring-[#1283EB] focus:ring-offset-2 focus:ring-offset-[#1a1a1a] pr-12 transition-all"
                    disabled={isLoading || !token}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60 transition-colors focus:outline-none focus:text-white"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" aria-hidden="true" />
                    ) : (
                      <Eye className="h-5 w-5" aria-hidden="true" />
                    )}
                  </button>
                </div>

                {/* Password Strength Indicator */}
                {password && passwordStrength && (
                  <div className="mt-3 space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${passwordStrength.color} transition-all duration-300`}
                          style={{ width: `${(passwordStrength.score / 5) * 100}%` }}
                          role="progressbar"
                          aria-valuenow={passwordStrength.score}
                          aria-valuemin={0}
                          aria-valuemax={5}
                          aria-label="Password strength"
                        />
                      </div>
                      <span className="text-xs text-white/60 min-w-[80px]">
                        {passwordStrength.label}
                      </span>
                    </div>
                    <ul id="password-requirements" className="text-xs text-white/50 space-y-1">
                      <li className={password.length >= 8 ? "text-green-400" : ""}>
                        At least 8 characters
                      </li>
                      <li className={/[A-Z]/.test(password) && /[a-z]/.test(password) ? "text-green-400" : ""}>
                        Uppercase and lowercase letters
                      </li>
                      <li className={/\d/.test(password) ? "text-green-400" : ""}>
                        At least one number
                      </li>
                      <li className={/[^A-Za-z0-9]/.test(password) ? "text-green-400" : ""}>
                        At least one special character
                      </li>
                    </ul>
                  </div>
                )}
              </div>

              {/* Confirm Password Input */}
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-white/80 mb-1.5">
                  Confirm New Password
                </label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    autoComplete="new-password"
                    required
                    aria-required="true"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your password"
                    className="h-12 bg-[#0a0a0a] border-white/10 text-white placeholder:text-white/40 focus:border-[#1283EB] focus:ring-2 focus:ring-[#1283EB] focus:ring-offset-2 focus:ring-offset-[#1a1a1a] pr-12 transition-all"
                    disabled={isLoading || !token}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60 transition-colors focus:outline-none focus:text-white"
                    aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-5 w-5" aria-hidden="true" />
                    ) : (
                      <Eye className="h-5 w-5" aria-hidden="true" />
                    )}
                  </button>
                </div>
                {confirmPassword && password !== confirmPassword && (
                  <p className="mt-1.5 text-xs text-red-400">Passwords do not match</p>
                )}
              </div>

              <Button
                type="submit"
                disabled={isLoading || !token}
                className="w-full h-13 bg-[#1283EB] hover:bg-[#1283EB]/90 text-white font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#1283EB] focus:ring-offset-2 focus:ring-offset-[#1a1a1a]"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" aria-hidden="true" />
                    Resetting password...
                  </>
                ) : (
                  "Reset Password"
                )}
              </Button>
            </form>
          </>
        )}

        {/* Footer */}
        <div className="text-center pt-4 border-t border-white/10">
          <p className="text-xs text-white/40">
            Need help?{" "}
            <a
              href="mailto:support@aiia.io"
              className="text-[#1283EB]/80 hover:text-[#1283EB] transition-colors"
            >
              Contact Support
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;
